import { Button, Flex } from "antd";
import { type StrictlyBasisPoints, stringFromBasisPoints } from "@vyne-shared/utils/currency";
import { showBulkRequestSubmitConfirmModal } from "./showBulkRequestConfirmModal";
import { useIsMutating } from "@tanstack/react-query";
import { useState } from "react";
import { validateEmail } from "@vyne-shared/utils/email";
import { validatePhoneString } from "../../bulkRequestUtils.phone";
import { validateRecord } from "../../bulkRequestUtils";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";

interface BulkRequestSubmitButtonProps {
    /** Are we currently submitting? */
    readonly isSubmitting: boolean;

    combinedData: readonly BulkRequestRecord[];

    selectedRows: Readonly<Set<string>>;

    mutate: (rows: BulkRequestRecord[]) => Promise<void>;
}

/** Bulk request submit button. */
export const BulkRequestSubmitButton = ({
    combinedData,
    selectedRows,
    mutate,
    isSubmitting,
}: BulkRequestSubmitButtonProps) => {
    const allowCommChanges = useIsMutating({ mutationKey: ["setAllowComm"] });

    const [confirming, setConfirming] = useState(false);

    const submitRows = combinedData.filter((row) => selectedRows.has(row.pmsId));
    const validSubmitRows = submitRows.filter((row) => validateRecord(row));
    const validSubmitAmount = validSubmitRows.reduce((sum, row) => sum + row.amount, 0) as StrictlyBasisPoints;
    const validCount = validSubmitRows.length;
    const invalidCount = submitRows.length - validCount;
    const submitLength = submitRows.length - invalidCount;
    const ready = validCount > 0;

    const submit = async () => {
        // if we enter here, validatePhoneString is guaranteed to return
        // a proper non-empty "+12125550100" formed phone for every row with requestChannel "SMS" or "Both",
        // and ensureValidEmail is guaranteed to return a non-empty valid email for every
        // row with requestChannel "Email" or "Both"
        return await mutate(
            validSubmitRows.map((row) => ({
                ...row,
                phone: validatePhoneString(row),
                email: ensureValidEmail(row.email),
            })),
        );
    };

    const requestsText = submitLength > 1 ? "Requests" : "Request";
    const text = submitLength > 0 ? `Send ${requestsText}` : "Select Request(s) To Send";

    return (
        <Flex className="vyne-tabs-content-header-requests" align="center" gap="8px">
            <span className="vyne-tabs-content-header-requests-summary">
                <strong>{submitLength}</strong> {requestsText},{" "}
                <strong>totaling {stringFromBasisPoints(validSubmitAmount)}</strong>
            </span>
            <Button
                type="primary"
                id="bulk-payment-request-send-btn"
                loading={isSubmitting || confirming}
                disabled={!ready || allowCommChanges > 0}
                className="vyne-tabs-content-header-cta-button"
                onClick={() => {
                    if (!isSubmitting && !confirming) {
                        setConfirming(true);
                        void showBulkRequestSubmitConfirmModal(setConfirming, submit, validSubmitRows, invalidCount);
                    }
                }}
            >
                {text}
            </Button>
        </Flex>
    );
};

/* If email address valid, return it unchanged. If invalid, return undefined. */
function ensureValidEmail(email: string | undefined) {
    return validateEmail(email) ? email : undefined;
}
